import { useDesignMaxTheme } from "@/packages";
import { usePensionNameValue } from "@/shared/client";
import { useGetCommon, type AttractionResponse } from "@/shared/server";
import { FONT } from "@/styles";
import { Box, Center, chakra, Flex, Spinner, Text } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

export const Travel = () => {
    const { data } = useGetCommon<AttractionResponse>({ section: "travels" });
    const pensionName = usePensionNameValue();
    const { COLORS, MEDIAS } = useDesignMaxTheme();
    const { ref, inView } = useInView({ threshold: 0.5, delay: 1, triggerOnce: true });

    const { isLaptop, isMobile } = MEDIAS();

    const boxPCHeight = isLaptop ? 528 : 792;
    const boxPCmaxWidth = isLaptop ? "546px" : "728px";
    const orangeBoxPCHeight = isLaptop ? 805 : 1142;
    const firstImgPCHeight = isLaptop ? 200 : 424;
    const secondImgPcHEIGHT = isLaptop ? 360 : 464;
    const thirdImgPCHeight = isLaptop ? 200 : 288;

    const laptopPaddingRight = isLaptop ? "88px" : "none";

    const engTitlePCfontSize = isLaptop ? 32 : 48;

    return data ? (
        <Flex ref={ref} position="relative" w="100%" h="100dvh" flexDir={isMobile ? "column-reverse" : "row"}>
            <Box flex="1" position="relative" display={isMobile ? "none" : "block"}></Box>
            <Flex
                paddingLeft="24px"
                position={isMobile ? "relative" : "absolute"}
                top={isMobile ? "none" : "50%"}
                maxW="1664px"
                w="100%"
                h={isMobile ? "100%" : boxPCHeight}
                justifyContent="flex-end"
                transform={isMobile ? "none" : "translateY(-50%)"}
                paddingRight={isMobile ? "24px" : laptopPaddingRight}
                zIndex="1"
                paddingY={isMobile ? "16px" : "0"}
            >
                <Flex
                    overflow="hidden"
                    maxW="1504px"
                    w="100%"
                    h="100%"
                    columnGap="48px"
                    flexDir={isMobile ? "column" : "row"}
                >
                    <Flex
                        flexDir="column"
                        justifyContent={isMobile ? "normal" : "space-between"}
                        rowGap={isMobile ? "16px" : "0"}
                        maxW={isMobile ? "none" : boxPCmaxWidth}
                        w="100%"
                        h="100%"
                        paddingTop={isMobile ? "0" : "48px"}
                    >
                        <MotionTextBox
                            display="flex"
                            flexDir="column"
                            rowGap="8px"
                            initial={{ y: "-100%" }}
                            animate={inView ? { y: ["-100%", 0], transition: { duration: 0.3, ease: "easeOut" } } : {}}
                        >
                            <Text {...FONT("cormorant", isMobile ? 20 : engTitlePCfontSize, 700, 1.2)}>
                                AROUND PENSION
                            </Text>
                            <Flex flexDir="column" rowGap="16px">
                                <Text {...FONT("notoSerif", isMobile ? 16 : 24, 500)}>
                                    다양한 주변관광지와 함께하는 휴식
                                </Text>
                                <Flex flexDir="column" rowGap="8px">
                                    <Text {...FONT("pretendard", isMobile ? 14 : 16, 500)}>
                                        다양한 주변 관광지에서 알찬 여행을 즐겨보세요.
                                    </Text>
                                    <Box
                                        sx={{
                                            "& p": {
                                                ...FONT("pretendard", 14, 300),
                                                color: COLORS.gray[500],
                                            },
                                        }}
                                    >
                                        <Text>다양한 즐길거리와 볼거리가 가득한 {pensionName}에서</Text>
                                        <Text>사랑하는 가족, 연인, 친구들과 평생 잊지못할 추억을 만들어 보세요.</Text>
                                    </Box>
                                </Flex>
                            </Flex>
                        </MotionTextBox>
                        <Flex
                            flexDir={isMobile ? "column" : "row"}
                            columnGap="24px"
                            rowGap={isMobile ? "16px" : "0"}
                            alignItems="flex-end"
                            height="100%"
                            sx={{
                                "& div": {
                                    borderRadius: "16px",
                                },
                            }}
                        >
                            <MotionFirstImg
                                flex={isLaptop ? "1" : "none"}
                                maxW={isLaptop ? "none" : "400px"}
                                w="100%"
                                h={isMobile ? "100%" : firstImgPCHeight}
                                initial={{ x: "-100%" }}
                                animate={
                                    inView ? { x: ["-100%", 0], transition: { duration: 0.3, ease: "easeOut" } } : {}
                                }
                                background={`url(${data.imgList[0].imgUrl})no-repeat center/cover`}
                            />
                            <MotionThirdImg
                                flex={isLaptop ? "1" : "none"}
                                maxW={isLaptop ? "none" : "304px"}
                                w="100%"
                                h={isMobile ? "100%" : thirdImgPCHeight}
                                initial={{ y: "100%" }}
                                animate={
                                    inView
                                        ? {
                                              y: ["100%", 0],
                                              transition: { duration: 0.3, ease: "easeOut", delay: 0.6 },
                                          }
                                        : {}
                                }
                                background={`url(${data.imgList[2].imgUrl})no-repeat center/cover`}
                            />
                        </Flex>
                    </Flex>
                    <Flex
                        display={isMobile ? "none" : "flex"}
                        maxW={isLaptop ? "546px" : "728px"}
                        w="100%"
                        flexDir="column"
                        rowGap="48px"
                    >
                        <MotionSecondImg
                            w="100%"
                            h={isMobile ? "auto" : secondImgPcHEIGHT}
                            background={`url(${data.imgList[1].imgUrl})no-repeat center/cover`}
                            borderRadius="16px"
                            initial={{ x: "100%" }}
                            animate={
                                inView
                                    ? { x: ["100%", 0], transition: { duration: 0.3, ease: "easeOut", delay: 0.4 } }
                                    : {}
                            }
                        />
                        <Box
                            sx={{
                                "& p": {
                                    ...FONT("cormorant", 20, 700, 1.2),
                                    color: COLORS.white,
                                },
                            }}
                        >
                            <Text>{`Don't wait up. Don't be shy about liking and loving someone.`}</Text>
                            <Text>Rather tahn living with an empty heart, love someone full of heart.</Text>
                        </Box>
                    </Flex>
                </Flex>
            </Flex>
            <Box
                w={isMobile ? "100%" : orangeBoxPCHeight}
                h={isMobile ? "60px" : "auto"}
                background={COLORS.orange}
                borderLeftRadius={isMobile ? "none" : "48px"}
                borderBottomLeftRadius={isMobile ? "32px" : "none"}
            />
        </Flex>
    ) : (
        <Center h="100vh" columnGap="8px">
            <Text>LOADING</Text>
            <Spinner />
        </Center>
    );
};

const MotionTextBox = chakra(motion.div);
const MotionFirstImg = chakra(motion.div);
const MotionSecondImg = chakra(motion.div);
const MotionThirdImg = chakra(motion.div);
